import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Box, Container, Typography, Grid, Card, Button, Chip, Breadcrumbs, Link, Fade, Dialog, DialogTitle, DialogContent, IconButton, Divider, Avatar, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle, People, Close, Star, Home, NavigateNext, School, Language, TrendingUp, EmojiEvents, Quiz, Assignment, Psychology } from '@mui/icons-material';
import ContactUsForm from '../components/ContactForm';
import course2 from '../assets/images/Courses3.webp';
import banner02 from '../assets/images/banner02.webp';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';


const examPreparationData = {
  id: 'exam-preparation',
  image: course2,
  title: 'Exam Preparation',
  subtitle: 'Comprehensive Test Preparation & Academic Excellence',
  description: 'Excel in standardized Chinese examinations with our expert-led preparation courses. We cover both <b>Singapore government education system</b> and <b>International curriculum system</b>. For example PSLE, O Level (SEC from 2027), A Level, IGCSE Chinese, IB Chinese, AP Chinese, and OPI assessments etc.',
  detailedDescription: [
    'Our Exam Preparation program is designed to help students excel in various standardized Chinese examinations. Whether you\'re preparing for PSLE, O Level, A Level, IGCSE, IB, AP Chinese, or OPI assessments, our comprehensive approach ensures you achieve your academic goals.',
    'Our experienced instructors understand the specific requirements and formats of each examination. We provide targeted preparation strategies, practice materials, and personalized feedback to maximize your performance and confidence on exam day.'
  ],
  features: [
    'PSLE Chinese Preparation',
    'O Level (SEC 2027) Chinese',
    'A Level Chinese Studies',
    'IGCSE Chinese Language',
    'IB Chinese A & B & AB Initio',
    'AP Chinese Language & Culture',
    'OPI (Oral Proficiency Interview)',
    'Mock Exams & Practice Tests'
  ],
  learningOutcomes: [
    'Master exam-specific strategies and techniques for each test format',
    'Develop strong reading comprehension and analytical skills',
    'Enhance writing abilities for different essay types and formats',
    'Improve listening and speaking skills for oral examinations',
    'Build confidence through extensive practice and mock exams',
    'Understand marking criteria and how to maximize scores'
  ],
  pricing: {
    online: '$80+/session',
    offline: '$100+/session',
    duration: '90 minutes per session',
    groupSize: 'one-on-one/group',
    groupDiscount: 'Group discounts available',
    package: '40 session+ package: 10% discount'
  },
  targetAudience: 'Students (Primary to University), Exam Candidates',
  level: 'All Levels',
  courseStructure: [
    {
      level: 'Primary Level (PSLE)',
      duration: '6-12 months',
      focus: 'Foundation building, basic exam techniques, confidence development',
      topics: [
        'PSLE Chinese paper structure and requirements',
        'Reading comprehension strategies',
        'Composition writing techniques',
        'Oral examination preparation',
        'Vocabulary and grammar mastery',
        'Time management skills'
      ]
    },
    {
      level: 'Secondary Level (O Level/IGCSE)',
      duration: '12-18 months',
      focus: 'Advanced comprehension, essay writing, cultural understanding',
      topics: [
        'O Level Chinese examination format',
        'Advanced reading comprehension',
        'Essay writing and argumentation',
        'Literature analysis and appreciation',
        'Cultural context and understanding',
        'Mock examination practice'
      ]
    },
    {
      level: 'Pre-University Level (A Level/IB/AP)',
      duration: '18-24 months',
      focus: 'Critical analysis, research skills, academic writing',
      topics: [
        'A Level Chinese Studies curriculum',
        'IB Chinese A & B & AB Initio requirements',
        'AP Chinese Language & Culture',
        'Advanced literary analysis',
        'Research methodology and academic writing',
        'Independent study skills'
      ]
    }
  ],
  teachingMethodology: [
    'Diagnostic assessment to identify strengths and weaknesses',
    'Personalized study plans based on individual needs',
    'Comprehensive practice with past papers and mock exams',
    'One-on-one feedback and progress tracking',
    'Group study sessions for peer learning',
    'Exam strategy workshops and time management training',
    'Stress management and confidence building techniques',
    'Regular progress assessments and goal setting'
  ],
  whyChooseUs: [
    'Experienced instructors with proven track records',
    'Comprehensive coverage of all major Chinese examinations',
    'Small class sizes for personalized attention',
    'Flexible scheduling to accommodate school commitments',
    'Extensive library of past papers and practice materials',
    'Regular mock exams with detailed feedback',
    'Success stories from previous students',
    'Ongoing support throughout the examination period'
  ],
  faq: [
    {
      question: 'Which examinations do you prepare students for?',
      answer: 'We prepare students for PSLE, O Level (SEC from 2027), A Level, IGCSE Chinese, IB Chinese A / B & AB Initio, AP Chinese Language & Culture, and OPI assessments.'
    },
    {
      question: 'How long should I start preparing before the exam?',
      answer: 'We recommend starting preparation 6-12 months before the exam date, depending on your current level and the specific examination requirements.'
    },
    {
      question: 'Do you provide mock exams and practice tests?',
      answer: 'Yes, we conduct regular mock exams and provide extensive practice tests to help students familiarize themselves with the exam format and timing.'
    },
    {
      question: 'Can I get personalized study plans?',
      answer: 'Absolutely! We create personalized study plans based on your current level, target examination, and learning goals.'
    },
    {
      question: 'What is the class size for exam preparation?',
      answer: 'We keep class sizes small (maximum 8 students) to ensure personalized attention and effective learning for each student.'
    }
  ]
};

function ExamPreparation() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [visibleSections, setVisibleSections] = useState({});
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = ['overview', 'structure', 'methodology', 'why-choose-us', 'pricing', 'faq'];
    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEOHead pageKey="exam-preparation" />
      <Box sx={{ 
        minHeight: '100vh', 
        position: 'relative'
      }}>

      {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '240px', md: '500px' },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Background Image with Overlay */}
        <Box
          component="img"
          src={banner02}
          alt="Exam Preparation Banner"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
        />
        
        {/* Content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minHeight: { xs: '240px', md: '500px' },
            pt: { xs: 2, md: 0 },
            px: { xs: 1, md: 4 }
          }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12}>
                <Box sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 60,
                    left: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    zIndex: -1
                  }
                }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '1.5rem',
                      lineHeight: 1.6,
                      letterSpacing: '0.1em',
                      mb: 4,
                      fontWeight: 500,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      display: { xs: 'none', md: 'block' }
                    }}
                  >
                    Achieve academic excellence with our comprehensive exam preparation
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: 700,
                      color: 'white',
                      mb: 5,
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      lineHeight: 1.1,
                      textShadow: '2px 2px 8px rgba(0,0,0,0.7)'
                    }}
                  >
                    Exam Preparation
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setEnrollDialogOpen(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        color: 'white',
                        px: 6,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        textTransform: 'none',
                        boxShadow: '0 8px 32px rgba(220, 38, 38, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 40px rgba(220, 38, 38, 0.6)'
                        },
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      Book Free Trial
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Breadcrumb Navigation */}
      <Box sx={{ 
        bgcolor: 'white', 
        py: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <Container maxWidth="xl">
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                color: '#5a6c7d'
              }
            }}
          >
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center'}}
              href="/"
            >
              <Home sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Link
              underline="hover"
              href="/courses"
            >
              Courses
            </Link>
            <Typography>
              Exam Preparation
            </Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      {/* Course Overview Section */}
      <Box id="overview" sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Fade in={visibleSections['overview']} timeout={800}>
            <Grid container spacing={ { xs: 4, md: 8 }}>
              <Grid item xs={12} md={5}>
                <Box
                  component="img"
                  src={examPreparationData.image}
                  alt="Exam Preparation Course"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: 700,
                      color: '#07284b',
                      mb: 3,
                      fontSize: { xs: '2rem', md: '2.5rem' }
                    }}
                  >
                    {examPreparationData.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#9f2122',
                      mb: { xs: 2, md: 4 },
                      fontWeight: 600,
                      fontSize: { xs: '1.2rem', md: '1.5rem' }
                    }}
                  >
                    {examPreparationData.subtitle}
                  </Typography>
                  
                  
                  <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Chip
                      icon={<People />}
                      label={`Target: ${examPreparationData.targetAudience}`}
                      sx={{
                        bgcolor: 'rgba(7, 40, 75, 0.1)',
                        color: '#07284b',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    />
                    <Chip
                      icon={<Star />}
                      label={`Level: ${examPreparationData.level}`}
                      sx={{
                        bgcolor: 'rgba(7, 40, 75, 0.1)',
                        color: '#07284b',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      mb: { xs: 2, md: 4 },
                      lineHeight: 1.8,
                      fontSize: '1.1rem',
                      '& b': {
                        fontWeight: 700,
                        color: '#07284b'
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: examPreparationData.description }}
                  />
                  
                  {examPreparationData.detailedDescription.map((paragraph, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{
                        color: '#5a6c7d',
                        mb: { xs: 2, md: 3 },
                        lineHeight: 1.8,
                        fontSize: '1.1rem'
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Course Structure */}
      <Box id="structure" sx={{ py: { xs: 6, md: 12 }, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Course Structure
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              Comprehensive preparation for all major Chinese examinations
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {examPreparationData.courseStructure.map((level, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: index === 0 
                        ? 'linear-gradient(90deg, #3b82f6, #1d4ed8)'
                        : index === 1 
                        ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                        : 'linear-gradient(90deg, #10b981, #059669)',
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
                      '& .level-icon': {
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      background: index === 0 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                        : index === 1 
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        zIndex: 0
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center'}}>
                        <Avatar
                          className="level-icon"
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            mr: 2,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {index === 0 ? <School /> : index === 1 ? <TrendingUp /> : <EmojiEvents />}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: '1.4rem',
                              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          >
                            {level.level}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            Duration: {level.duration}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ p: 4, bgcolor: 'white' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 3,
                        fontSize: '1.1rem',
                        lineHeight: 1.4
                      }}
                    >
                      Focus Areas
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        fontSize: '0.95rem',
                        lineHeight: 1.5
                      }}
                    >
                      {level.focus}
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: '#e2e8f0' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Key Topics
                    </Typography>
                    <Box>
                      {level.topics.map((topic, topicIndex) => (
                        <Box key={topicIndex} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: index === 0 ? '#3b82f6' : index === 1 ? '#f59e0b' : '#10b981',
                              mt: 0.5,
                              mr: 2,
                              flexShrink: 0,
                              boxShadow: `0 0 0 2px ${index === 0 ? 'rgba(59, 130, 246, 0.2)' : index === 1 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              fontSize: '0.9rem',
                              lineHeight: 1.5
                            }}
                          >
                            {topic}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Course Features - Redesigned */}
      <Box sx={{ 
        py: { xs: 6, md: 12 }, 
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(7, 40, 75, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(159, 33, 34, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}>
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Why Choose Our Exam Preparation Program?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.2rem',
                fontWeight: 500
              }}
            >
              Comprehensive preparation for all major Chinese examinations with proven results
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {examPreparationData.features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: index % 2 === 0 
                        ? 'linear-gradient(90deg, #07284b, #1e4a7c)'
                        : 'linear-gradient(90deg, #9f2122, #dc2626)',
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', mb: 3 }}>
                      <Avatar
                        className="feature-icon"
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: index % 2 === 0 ? '#07284b' : '#9f2122',
                          background: index % 2 === 0 
                            ? 'linear-gradient(135deg, #07284b, #1e4a7c)'
                            : 'linear-gradient(135deg, #9f2122, #dc2626)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {index === 0 ? <School /> : index === 1 ? <Quiz /> : index === 2 ? <Assignment /> : index === 3 ? <Language /> : index === 4 ? <Psychology /> : index === 5 ? <TrendingUp /> : index === 6 ? <EmojiEvents /> : <CheckCircle />}
                      </Avatar>
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '1.2rem',
                        lineHeight: 1.4
                      }}
                    >
                      {feature}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '0.95rem',
                        lineHeight: 1.5
                      }}
                    >
                      {index === 0 && "Comprehensive preparation for Primary School Leaving Examination"}
                      {index === 1 && "Thorough coverage of O Level Chinese and SEC 2027 requirements"}
                      {index === 2 && "Advanced preparation for A Level Chinese Studies curriculum"}
                      {index === 3 && "Complete IGCSE Chinese Language examination preparation"}
                      {index === 4 && "Specialized IB Chinese A & B & AB Initio program preparation"}
                      {index === 5 && "AP Chinese Language & Culture examination mastery"}
                      {index === 6 && "Professional OPI assessment preparation and practice"}
                      {index === 7 && "Extensive mock exams and practice test sessions"}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Learning Outcomes */}
      <Box sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Learning Outcomes
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              What you will achieve upon completion of our Exam Preparation program
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {examPreparationData.learningOutcomes.map((outcome, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 0, md: 2 } }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#9f2122',
                      mt: 1,
                      mr: 3,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                      fontSize: '1.1rem'
                    }}
                  >
                    {outcome}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section - Moved before FAQ */}
      <Box id="pricing" sx={{ 
        py: { xs: 6, md: 8 }, 
        bgcolor: '#f8fafc'
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'serif',
                fontWeight: 600,
                color: '#1e293b',
                mb: 2,
                fontSize: { xs: '1.8rem', md: '2,2rem' }
              }}
            >
              Investment Options
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1rem'
              }}
            >
              Choose the learning format that works best for you
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {/* Online Package */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid rgba(7, 40, 75, 0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: '#07284b'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: '#07284b',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '1.8rem' }
                  }}
                >
                  Online Learning
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  From $80+ per session
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Interactive video sessions" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Digital materials included" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Flexible scheduling" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Exam preparation focus" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

            {/* Offline Package */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid #9f2122',
                  position: 'relative',
                  bgcolor: 'rgba(159, 33, 34, 0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(159, 33, 34, 0.2)'
                  }
                }}
              >
                <Chip
                  label="Most Popular"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#9f2122',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    color: '#9f2122',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '1.8rem' },
                    mt: 2
                  }}
                >
                  Offline Learning
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  From $100+ per session
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Face-to-face interaction" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Physical materials provided" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Immediate feedback" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Intensive exam practice" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

            {/* Group Package */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid rgba(7, 40, 75, 0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: '#07284b'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: '#07284b',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '1.8rem' },
                  }}
                >
                  Group Learning
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  Group discounts available
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Collaborative learning" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Peer interaction" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Cost-effective option" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Shared exam strategies" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>

          {/* Special Offers */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Card
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.05) 0%, rgba(159, 33, 34, 0.03) 100%)',
                border: '1px solid rgba(159, 33, 34, 0.2)',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#07284b',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Special Package Offers
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Chip
                    label="20-session program: 10% discount"
                    sx={{
                      bgcolor: '#9f2122',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                      px: 1
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Chip
                    label="Free trial session available"
                    sx={{
                      bgcolor: '#07284b',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                      px: 1
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              Find answers to common questions about our Exam Preparation program
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {examPreparationData.faq.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      borderColor: '#9f2122'
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#07284b',
                      fontWeight: 700,
                      mb: 3,
                      fontSize: '1.2rem'
                    }}
                  >
                    Q: {item.question}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                      fontSize: '1.1rem'
                    }}
                  >
                    A: {item.answer}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 12 },
          background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Ready to Excel in Your Chinese Examinations?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Join thousands of students who have achieved their academic goals with our comprehensive exam preparation program
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setEnrollDialogOpen(true)}
              sx={{
                bgcolor: 'white',
                color: '#07284b',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Start Your Preparation
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Contact Form Dialog */}
      <Dialog
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
        maxWidth="md"
        fullScreen={isSmallScreen}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: {xs: 2, md: 4},
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}
          >
            Enroll in Exam Preparation
          </Typography>
          <IconButton
            onClick={() => setEnrollDialogOpen(false)}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#9f2122',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#8a1e1f'
            }
          }}
        >
          <Box sx={{ px: 4, py: { xs: 4, md: 8 } }}>
            <ContactUsForm />
          </Box>
        </DialogContent>
      </Dialog>
      </Box>
    </>
  );
}

export default ExamPreparation;